import { gql } from 'urql';
import { BranchInfo } from '../fragments/branch.graphql';
import { UserBasicInfo } from '../fragments/user.graphql';

export const getPlayerWithOrganizations = gql`
  query GetPlayerWithOrganizations($playerId: Int!) {
    user(userId: $playerId) {
      ...UserBasicInfo
      organizations {
        id
        name
        role
        slug
        shorthand
        branch {
          id
          name
          shorthand
        }
      }
    }
  }
  ${UserBasicInfo}
`;

export const getPlayerWithCharactersInOrganization = gql`
  query GetPlayerWithCharactersInOrganization($playerId: Int!) {
    user(userId: $playerId) {
      ...UserBasicInfo
      notes
      emailCorrespondence
      mailingAddress
      maxCharactersCount
      maxActiveCharactersCount
      lastMembership {
        id
        endsAt
      }
      characters {
        id
        name
        status
        buildEarned
        buildUsed
        version
        canBeDeleted
      }
      upcomingEvents {
        id
        checkinApproved
        buildAwarded
        event {
          id
          name
          startsAt
          location {
            id
            name
          }
        }
      }
    }
  }
  ${UserBasicInfo}
`;

export const getPlayerPerks = gql`
  query GetPlayerPerks($playerId: Int!) {
    user(userId: $playerId) {
      id
      caps {
        id
        grantor {
          id
          fullName
        }
        branch {
          id
          shorthand
        }
        state
        reason
        amount
        createdAt
      }
    }
  }
`;

export const getPlayerCorrectiveActions = gql`
  query GetPlayerCorrectiveActions($playerId: Int!) {
    user(userId: $playerId) {
      id
      correctiveActions {
        id
        issuer {
          id
          fullName
        }
        branch {
          id
          shorthand
        }
        kind
        startsAt
        endsAt
        reason
      }
    }
  }
`;

export const getPlayerOwnedBranches = gql`
  query GetPlayerOwnedBranches($playerId: Int!) {
    user(userId: $playerId) {
      id
      ownedBranches {
        id
        name
      }
    }
  }
`;

export const getPlayerMemberships = gql`
  query GetPlayerMemberships($playerId: Int!) {
    user(userId: $playerId) {
      id
      memberships {
        id
        grantor {
          id
          fullName
        }
        grantingBranch {
          id
          shorthand
        }
        startsAt
        endsAt
      }
    }
  }
`;

export const getPlayerReferrals = gql`
  query GetPlayerReferrals($playerId: Int!) {
    user(userId: $playerId) {
      id
      invitations {
        id
        createdAt
        newbie {
          id
          fullName
        }
        inviter {
          id
          fullName
        }
      }
      referrals {
        id
        createdAt
        newbie {
          id
          fullName
        }
        inviter {
          id
          fullName
        }
      }
    }
  }
`;

export const getPlayerAttendances = gql`
  query GetPlayerAttendances($playerId: Int!, $from: String, $upto: String) {
    user(userId: $playerId) {
      id
      eventAttendances(from: $from, upto: $upto) {
        id
        buildAwarded
        buildGrowth
        checkinApproved
        kind
        paid
        attending
        attendeeCharacters {
          id
          buildGrowth
          character {
            id
            name
          }
        }
        event {
          id
          name
          startsAt
          endsAt
          registrationOpensAt
          kind
          isVirtual
          branch {
            ...BranchInfo
          }
        }
      }
    }
  }
  ${BranchInfo}
`;

export const logisticsSearchPlayer = gql`
  query LogisticsSearchPlayer($query: String!, $eventId: Int!) {
    searchPlayer(query: $query, eventId: $eventId) {
      ... on User {
        __typename
        id
        firstName
        lastName
        fullName
        preferredName
        characters(checkinableOnly: true) {
          id
          name
        }
      }
      ... on Character {
        __typename
        id
        name
        user {
          id
          fullName
          preferredName
        }
      }
      ... on Error {
        error
      }
    }
  }
`;

export const searchPlayers = gql`
  query SearchPlayer(
    $query: String!
    $playersOnly: Boolean
    $accessLevelFilters: [OrganizationRoleEnum!]
    $branchId: Int
    $modId: Int
    $eventId: Int
    $branchIds: [Int!]
    $activeFrom: String
    $activeUpto: String
    $activeInversion: Boolean
    $withCorrectiveActions: Boolean
  ) {
    searchPlayer(
      query: $query
      playersOnly: $playersOnly
      accessLevelFilters: $accessLevelFilters
      branchId: $branchId
      modId: $modId
      eventId: $eventId
      branchIds: $branchIds
      activeFrom: $activeFrom
      activeUpto: $activeUpto
      activeInversion: $activeInversion
      withCorrectiveActions: $withCorrectiveActions
    ) {
      ... on User {
        __typename
        id
        createdAt
        firstName
        lastName
        fullName
        preferredName
        emailAddress
        characters(checkinableOnly: true) {
          id
          name
        }
        userOrganization {
          id
          role
          assistantFlags
        }
      }
      ... on Character {
        __typename
        id
        name
        user {
          id
          firstName
          lastName
          fullName
          preferredName
          userOrganization {
            id
            role
            assistantFlags
          }
        }
      }
      ... on Error {
        error
      }
    }
  }
`;

export const searchOnlyPlayers = gql`
  query SearchOnlyPlayer(
    $query: String!
    $playersOnly: Boolean
    $accessLevelFilters: [OrganizationRoleEnum!]
    $branchId: Int
    $modId: Int
    $eventId: Int
    $branchIds: [Int!]
    $activeFrom: String
    $activeUpto: String
    $activeInversion: Boolean
    $withCorrectiveActions: Boolean
  ) {
    searchPlayer(
      query: $query
      playersOnly: $playersOnly
      accessLevelFilters: $accessLevelFilters
      branchId: $branchId
      modId: $modId
      eventId: $eventId
      branchIds: $branchIds
      activeFrom: $activeFrom
      activeUpto: $activeUpto
      activeInversion: $activeInversion
      withCorrectiveActions: $withCorrectiveActions
    ) {
      ... on User {
        __typename
        id
        createdAt
        firstName
        lastName
        fullName
        preferredName
        emailAddress
      }
      ... on Character {
        __typename
        id
      }
      ... on Error {
        error
      }
    }
  }
`;
